import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { arrayRemove, arrayPush, change, getFormValues } from 'redux-form';
import { withTranslation, useTranslation } from 'react-i18next';
import { findIndex, get, size } from 'lodash-es';
import { useSelector } from 'react-redux';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';

import { MultiSelect } from './multi-select';
import { ValidationError } from 'pages/upload.page/publish-video/fragments';
import { ToolTip } from 'components/forms/tool-tip';

/**
 * @param {function} dispatch
 * @param {boolean} isMulti
 * @param {string} formName
 * @param {string} name
 * @param {*} params
 * @param {*}data
 * @return {*}
 */
const onChangeHandler = (dispatch, isMulti, formName, name, formValues, params, data) => {
  const { action, option, removedValue } = data;

  if (isMulti) {
    if (action === 'create-option') {
      const lastElementValue = params[params.length - 1].value;
      return dispatch(arrayPush(formName, name, lastElementValue));
    }

    if (action === 'select-option') {
      return dispatch(arrayPush(formName, name, option.value));
    }

    if (action === 'clear') {
      return dispatch(change(formName, name, []));
    }

    const removeItem = (itemId) => {
      const idx = findIndex(get(formValues, name), (id) => id === itemId);
      if (idx !== -1) {
        return dispatch(arrayRemove(formName, name, idx));
      }
    };

    if (action === 'deselect-option') {
      return removeItem(option.id);
    }

    if (removedValue) {
      return removeItem(removedValue.id);
    } else if (action === 'pop-value') {
      // Backspace when there is no option selected should do nothing
      return;
    }
  }

  return dispatch(change(formName, name, [get(params, 'value', null)]));
};

const Label = ({ text, required, hashtagSupport }) => {
  const { t } = useTranslation();

  return (
    <label className="label">
      {text.length === 0 ? t('publish_in') : text}
      {required ? ' *' : ''}
      {hashtagSupport && (
        <ToolTip
          id={'video-keyword-tooltip'}
          icon={faQuestionCircle}
          text={t('video_metadata_keywords_hashtag_tooltip')}
        />
      )}
    </label>
  );
};

const Select = ({
  t,
  label,
  meta: { error },
  required,
  hashtagSupport,
  options = [],
  isMulti,
  optimizeSearch,
  closeMenuOnSelect,
  creatableOptions,
  name,
  formName,
  selectedValues,
  dispatch,
  disabled = false,
}) => {
  const formValues = useSelector(getFormValues(formName));

  const [inputValue, setInputValue] = useState();
  const [visibleOptions, setVisibleOptions] = useState([...options]);

  useEffect(() => {
    let newOptions;

    if (optimizeSearch) {
      if (size(inputValue) < 2) {
        newOptions = [];
      } else if (size(inputValue) === 2) {
        // To optimize select performance we keep only matching keywords
        newOptions = options.filter((o) => o.label.toLowerCase().includes(inputValue.toLowerCase()));
      }
    }

    setVisibleOptions(newOptions || options);
  }, [optimizeSearch, inputValue, options]);

  return (
    <div className="form-group" data-e2e={name}>
      {label && <Label text={label} required={required} hashtagSupport={hashtagSupport} />}
      <MultiSelect
        options={visibleOptions}
        placeholder={!disabled ? t('please_select') : ''}
        noOptionsMessage={() => t('type_to_search')}
        selectedValues={selectedValues}
        isMulti={isMulti}
        onChange={onChangeHandler.bind(this, dispatch, isMulti, formName, name, formValues)}
        isDisabled={disabled}
        onInputChange={(value) => setInputValue(value)}
        closeMenuOnSelect={closeMenuOnSelect}
        creatableOptions={creatableOptions}
      />
      <ValidationError error={error} />
    </div>
  );
};

export const SelectDecorated = compose(
  connect(null, (dispatch) => ({ dispatch })),
  withTranslation()
)(Select);

SelectDecorated.propTypes = {
  options: PropTypes.array.isRequired,
  creatableOptions: PropTypes.bool,
  disabled: PropTypes.bool,
};
